import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, TextField, InputAdornment, Button, IconButton,
  Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';

function PatientCard({ patient, selectMode, selected, onToggle, onOpen, onDownload }) {
  const [hover, setHover] = useState(false);
  const { t } = useTranslation();

  return (
    <Box
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={selectMode ? () => onToggle(patient.id) : () => onOpen?.(patient)}
      sx={{
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        cursor: 'pointer',
        border: `2px solid ${selected ? '#e85878' : hover ? 'rgba(124,106,247,0.5)' : 'var(--border)'}`,
        transition: 'border-color 0.15s, transform 0.15s',
        transform: hover && !selectMode ? 'translateY(-3px)' : 'none',
        boxShadow: hover && !selectMode ? '0 12px 40px rgba(0,0,0,0.5)' : '0 4px 16px rgba(0,0,0,0.3)',
        bgcolor: 'var(--bg-card)',
        aspectRatio: '3/4',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Checkbox — select mode */}
      {selectMode && (
        <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 4 }}>
          <Checkbox
            checked={!!selected}
            onChange={() => onToggle(patient.id)}
            onClick={e => e.stopPropagation()}
            sx={{
              bgcolor: 'rgba(0,0,0,0.5)', borderRadius: 1, p: 0.25,
              color: 'rgba(255,255,255,0.5)',
              '&.Mui-checked': { color: '#e85878' },
            }}
          />
        </Box>
      )}

      {/* Big photo / placeholder */}
      <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', bgcolor: 'var(--bg-surface)' }}>
        {patient.photo ? (
          <img
            src={patient.photo}
            alt={patient.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.3s ease' }}
          />
        ) : (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <PersonOutlineIcon sx={{ fontSize: 72, color: 'rgba(255,255,255,0.08)' }} />
          </Box>
        )}

        {/* Hover action overlay */}
        {!selectMode && hover && (
          <Box sx={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.0) 50%, rgba(0,0,0,0.55) 100%)',
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            pb: 1.5,
          }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={e => { e.stopPropagation(); onDownload?.(patient); }}
              sx={{
                textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 2.5,
                bgcolor: '#fff', color: '#111328',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.88)' },
                fontSize: 12,
              }}
            >
              {t('myPatients.downloadToSmileFy')}
            </Button>
          </Box>
        )}
      </Box>

      {/* Footer — name + type */}
      <Box sx={{
        px: 1.5, py: 1.25,
        borderTop: '1px solid var(--border)',
        bgcolor: 'var(--bg-card)',
        flexShrink: 0,
      }}>
        <Typography sx={{
          color: 'var(--text-1)', fontWeight: 600, fontSize: 14,
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          lineHeight: 1.3,
        }}>
          {patient.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.4 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
            {patient.createdAt}
          </Typography>
          {patient.fileCount != null && (
            <Typography sx={{ color: '#7c6af7', fontSize: 11, fontWeight: 600 }}>
              {t('myPatients.files', { count: patient.fileCount })}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default function MyPatients({ patients = [], setPatients, onOpenPatient }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const filtered = useMemo(() =>
    patients.filter(p => p.name.toLowerCase().includes(search.toLowerCase())),
    [search, patients]
  );

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(p => p.id)));
  };

  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };

  const handleDeleteConfirm = () => {
    setPatients?.(prev => prev.filter(p => !selected.has(p.id)));
    setConfirmOpen(false);
    exitSelectMode();
  };

  const allSelected = filtered.length > 0 && selected.size === filtered.length;

  return (
    <Box sx={{ p: 3, height: '100%', overflowY: 'auto', bgcolor: 'var(--bg-surface)' }}>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <TextField
          placeholder={t('myPatients.searchPlaceholder')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'rgba(255,255,255,0.4)' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: 1, maxWidth: 500,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'var(--bg-input)', borderRadius: 2,
              '& fieldset': { borderColor: 'var(--border)' },
              '&:hover fieldset': { borderColor: 'rgba(124,106,247,0.4)' },
              '&.Mui-focused fieldset': { borderColor: '#7c6af7' },
            },
            '& .MuiInputBase-input': { color: 'var(--text-1)', fontSize: 15 },
            '& .MuiInputBase-input::placeholder': { color: 'var(--text-3)', opacity: 1 },
          }}
        />

        {selectMode ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            <Checkbox
              checked={allSelected}
              indeterminate={selected.size > 0 && !allSelected}
              onChange={toggleSelectAll}
              sx={{ color: 'rgba(255,255,255,0.4)', '&.Mui-checked': { color: '#e85878' }, '&.MuiCheckbox-indeterminate': { color: '#e85878' } }}
            />
            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, minWidth: 80 }}>
              {t('myPatients.selected', { count: selected.size })}
            </Typography>
            <Button
              variant="contained"
              startIcon={<DeleteOutlineIcon />}
              disabled={selected.size === 0}
              onClick={() => setConfirmOpen(true)}
              sx={{
                bgcolor: '#e85878', textTransform: 'none', fontWeight: 600,
                '&:hover': { bgcolor: '#c43058' },
                '&.Mui-disabled': { bgcolor: 'rgba(232,88,120,0.2)', color: 'rgba(255,255,255,0.3)' },
              }}
            >
              {t('myPatients.deleteCount', { count: selected.size })}
            </Button>
            <Tooltip title={t('myPatients.cancel')}>
              <IconButton onClick={exitSelectMode} sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#fff' } }}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Tooltip title={t('myPatients.deletePatients')} sx={{ ml: 'auto' }}>
            <IconButton
              onClick={() => setSelectMode(true)}
              sx={{
                ml: 'auto',
                color: '#f87171', bgcolor: 'rgba(248,113,113,0.08)',
                border: '1px solid rgba(248,113,113,0.2)', borderRadius: 2,
                '&:hover': { bgcolor: 'rgba(248,113,113,0.18)', borderColor: '#f87171' },
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 2,
      }}>
        {filtered.map(patient => (
          <PatientCard
            key={patient.id}
            patient={patient}
            selectMode={selectMode}
            selected={selected.has(patient.id)}
            onToggle={toggleSelect}
            onOpen={p => onOpenPatient?.(p)}
            onDownload={p => alert(`Downloading: ${p.name}`)}

          />
        ))}
        {filtered.length === 0 && (
          <Typography sx={{ color: 'rgba(255,255,255,0.3)', gridColumn: '1/-1', textAlign: 'center', mt: 6, fontSize: 15 }}>
            {t('myPatients.noPatients')}
          </Typography>
        )}
      </Box>

      {/* Delete confirmation */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: '#1a1a3e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 2 } }}
      >
        <DialogTitle sx={{ color: '#fff', fontWeight: 600, fontSize: 18 }}>{t('myPatients.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
            {t('myPatients.deleteConfirmMultiple', { count: selected.size })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none' }}>
            {t('myPatients.cancel')}
          </Button>
          <Button
            variant="contained" onClick={handleDeleteConfirm}
            startIcon={<DeleteOutlineIcon />}
            sx={{ textTransform: 'none', bgcolor: '#e85878', '&:hover': { bgcolor: '#c43058' } }}
          >
            {t('myPatients.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
